import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobal } from 'reactn';
import store, { showToastWithType } from '../../../../core/store';
import { isPermitted } from '../../../../../components/permission-control/permission-control';
import {
  createCasePlan,
  fetchCurrentUser,
  fetchPlanTemplates,
  fetchPlanTypes,
} from '../../services/case-plan-create.service';
import {
  buildCreateCasePlanErrorToast,
  buildCreateCasePlanPayload,
  buildCreateCasePlanToast,
  buildDefaultEditorsFromUser,
  canSubmitCasePlan,
  getEligibleResidentsForCreate,
  getFirstCreatedPlanId,
  isTemplatePlanType,
  PLAN_STRUCTURE_EMPTY,
  PLAN_STRUCTURE_TEMPLATE,
  RESIDENT_PLAN_TYPE,
  resolvePostCreateNavigationPath,
  TEMPLATE_PLAN_TYPE,
  summarizeCreateCasePlanResults,
} from '../../utils/case-plan-create.util';
import {
  CASE_PLAN_REENTRY_CREATE_PERMISSION,
  REENTRY_PLANS_LIST_PATH,
} from '../../constants/reentry-planning.constants';

const PLAN_TYPE_FADE_MS = 200;

/**
 * Orchestrates Create Plan / Create Template form state and actions.
 */
export const useCasePlanCreateHook = () => {
  const navigate = useNavigate();
  const [global, setGlobal] = useGlobal();
  const fadeTimerRef = useRef(null);
  const { cbacConfigFlag, permissions, permissionIds, isLoading: isGlobalLoadingFlag } = global;

  const canAccessCreate = useMemo(
    () =>
      isPermitted(
        cbacConfigFlag,
        permissions,
        CASE_PLAN_REENTRY_CREATE_PERMISSION,
        permissionIds,
      ),
    [cbacConfigFlag, permissionIds, permissions],
  );
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSettingsVisible, setIsSettingsVisible] = useState(true);

  const [selectedPlanType, setSelectedPlanType] = useState(RESIDENT_PLAN_TYPE);
  const [selectedResidents, setSelectedResidents] = useState([]);
  const [templateName, setTemplateName] = useState('');
  const [planStructure, setPlanStructure] = useState(PLAN_STRUCTURE_TEMPLATE);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editors, setEditors] = useState([]);

  const isTemplateMode = isTemplatePlanType(selectedPlanType);

  const navigateToPlansList = useCallback(() => {
    navigate(REENTRY_PLANS_LIST_PATH);
  }, [navigate]);

  useEffect(() => {
    if (!canAccessCreate) {
      return undefined;
    }

    let isMounted = true;

    const loadInitialData = async () => {
      setIsLoading(true);
      try {
        // allSettled so a template-list failure (CC-14989) still degrades to
        // Resident Plan + Empty structure instead of blanking the form (AC#1).
        const [typesResult, templateListResult, userResult] =
          await Promise.allSettled([
            fetchPlanTypes(),
            fetchPlanTemplates(),
            fetchCurrentUser(),
          ]);

        if (!isMounted) {
          return;
        }

        const types =
          typesResult.status === 'fulfilled'
            ? typesResult.value
            : [RESIDENT_PLAN_TYPE];
        const templateList =
          templateListResult.status === 'fulfilled'
            ? templateListResult.value
            : [];
        const user =
          userResult.status === 'fulfilled' ? userResult.value : null;
        const templatesUnavailable =
          templateListResult.status === 'rejected' || templateList.length === 0;

        setTemplates(templateList);
        setSelectedPlanType(types[0] || RESIDENT_PLAN_TYPE);
        setSelectedTemplate(templateList[0] || null);
        if (templatesUnavailable) {
          setPlanStructure(PLAN_STRUCTURE_EMPTY);
        }
        setEditors(buildDefaultEditorsFromUser(user));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      isMounted = false;
      if (fadeTimerRef.current) {
        window.clearTimeout(fadeTimerRef.current);
      }
    };
  }, [canAccessCreate]);

  const canCreate = useMemo(
    () =>
      canSubmitCasePlan({
        selectedResidents,
        selectedPlanType,
        planStructure,
        selectedTemplate,
        templateName,
      }) && !isSubmitting,
    [
      selectedResidents,
      selectedPlanType,
      planStructure,
      selectedTemplate,
      templateName,
      isSubmitting,
    ],
  );

  const pageTitle = isTemplateMode
    ? 'Create a New Template'
    : 'Create a New Plan';

  const structureSectionTitle = isTemplateMode
    ? 'Starting Structure'
    : 'Plan Structure';

  const createLabel = useMemo(() => {
    if (isTemplateMode) {
      return 'Create Template';
    }

    return selectedResidents.length > 1 ? 'Create Plans' : 'Create Plan';
  }, [isTemplateMode, selectedResidents.length]);

  const handleSwitchPlanType = useCallback(() => {
    if (fadeTimerRef.current) {
      window.clearTimeout(fadeTimerRef.current);
    }

    setIsSettingsVisible(false);
    fadeTimerRef.current = window.setTimeout(() => {
      setSelectedPlanType((current) =>
        isTemplatePlanType(current) ? RESIDENT_PLAN_TYPE : TEMPLATE_PLAN_TYPE,
      );
      setIsSettingsVisible(true);
      fadeTimerRef.current = null;
    }, PLAN_TYPE_FADE_MS);
  }, []);

  const handleResidentsChange = useCallback((residents) => {
    setSelectedResidents(Array.isArray(residents) ? residents : []);
  }, []);

  const handleTemplateNameChange = useCallback((nextName) => {
    setTemplateName(nextName);
  }, []);

  const handlePlanStructureChange = useCallback((structure) => {
    setPlanStructure(structure);
  }, []);

  const handleTemplateChange = useCallback((template) => {
    setSelectedTemplate(template);
  }, []);

  const handleEditorsChange = useCallback((nextEditors) => {
    setEditors(nextEditors);
  }, []);

  const handleCreatePlan = useCallback(async () => {
    if (!canCreate) {
      return;
    }

    setIsSubmitting(true);
    setGlobal({ isLoading: true });
    try {
      const payload = buildCreateCasePlanPayload({
        selectedResidents,
        selectedPlanType,
        planStructure,
        selectedTemplate,
        editors,
      });
      const eligibleResidents = getEligibleResidentsForCreate(selectedResidents);
      const result = await createCasePlan(payload);
      const summary = summarizeCreateCasePlanResults(result);
      const toast = buildCreateCasePlanToast(summary);
      store.dispatch(showToastWithType(toast.message, toast.type));

      const redirectPath = resolvePostCreateNavigationPath({
        createdCount: summary.created.length,
        eligibleResidentCount: eligibleResidents.length,
        firstPlanId: getFirstCreatedPlanId(result),
      });
      if (redirectPath) {
        navigate(redirectPath);
      }
    } catch (error) {
      const toast = buildCreateCasePlanErrorToast(error);
      store.dispatch(showToastWithType(toast.message, toast.type));
    } finally {
      setGlobal({ isLoading: false });
      setIsSubmitting(false);
    }
  }, [
    canCreate,
    selectedResidents,
    selectedPlanType,
    planStructure,
    selectedTemplate,
    editors,
    navigate,
    setGlobal,
  ]);

  return {
    canAccessCreate,
    isLoading,
    isGlobalLoading: Boolean(isGlobalLoadingFlag),
    isSubmitting,
    canCreate,
    isTemplateMode,
    isSettingsVisible,
    planTypeFadeMs: PLAN_TYPE_FADE_MS,
    pageTitle,
    structureSectionTitle,
    createLabel,
    selectedPlanType,
    selectedResidents,
    templateName,
    planStructure,
    templates,
    selectedTemplate,
    editors,
    handleBack: navigateToPlansList,
    handleCancel: navigateToPlansList,
    handleSwitchPlanType,
    handleResidentsChange,
    handleTemplateNameChange,
    handlePlanStructureChange,
    handleTemplateChange,
    handleEditorsChange,
    handleCreatePlan,
  };
};
